import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Calendar,
  MapPin,
  Pencil,
  SquareCheckBig,
  User,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { Badge, FullScreenSheet } from '@/components/ui';
import { ChangeLine } from '@/components/domain/shared/ReviewItemCard';
import { ReviewSources } from '@/components/domain/shared/ReviewSources';
import { ChildBadge } from '@/pages/review/_components';
import { REVIEW_COPY } from '@/pages/review/reviewCopy';
import type { ReviewListItem } from '@/pages/review/reviewListModel';
import type { Email } from '@/types';

export type DetailsSection = 'top' | 'sources';

export interface ReviewItemDetailsSources {
  /** Newest first. */
  emails: readonly Email[];
  summary: string | null;
  loading: boolean;
  error: boolean;
  reload(): void;
}

export interface ReviewItemDetailsSheetProps {
  /** null = closed (renders nothing). */
  item: ReviewListItem | null;
  /** 'sources' (AC15): scrolled to Sources with the newest email open once the emails land. */
  initialSection: DetailsSection;
  sources: ReviewItemDetailsSources;
  /** Step 2 fills it; empty hides the row (R15). */
  memberNames?: readonly string[];
  onClose(): void;
  /** The pencil → today's Edit Details. */
  onEdit(item: ReviewListItem): void;
  /**
   * A Confirm all is in flight: an edit then would race the batch. The
   * pencil stays focusable (aria-disabled) but does nothing.
   */
  editDisabled?: boolean;
}

function DetailRow({
  icon: Icon,
  label,
  children,
}: {
  icon: LucideIcon;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex shrink-0 items-center gap-1.5 text-sm text-muted-foreground">
        <Icon
          size={16}
          strokeWidth={1.5}
          aria-hidden="true"
        />
        <span>{label}</span>
      </div>
      <div className="flex min-w-0 flex-wrap justify-end gap-1 text-right">
        {children}
      </div>
    </div>
  );
}

function People({ names }: { names: readonly string[] }) {
  return names.map((name) => (
    <ChildBadge
      key={name}
      name={name}
    />
  ));
}

/**
 * Review item details (YAR-551 AC10, AC13–AC17; Figma 11597-27594).
 *
 * Read-only: title, date pill, the Change box or the cancel note, the
 * filled rows, then Sources. No action foot (AC16) — the pencil opens
 * today's Edit Details. Opened from the card's source line it lands at
 * Sources with the newest email open (AC15), once the emails have loaded.
 *
 * The page keys it by item id so each opening starts clean.
 */
export function ReviewItemDetailsSheet({
  item,
  initialSection,
  sources,
  memberNames = [],
  onClose,
  onEdit,
  editDisabled = false,
}: ReviewItemDetailsSheetProps) {
  const [openEmailId, setOpenEmailId] = useState<string | null>(null);
  const sourcesRef = useRef<HTMLElement>(null);
  const landed = useRef(false);
  const newestId = sources.emails[0]?.id ?? null;

  useEffect(() => {
    if (landed.current || initialSection !== 'sources' || !newestId) return;
    landed.current = true;
    setOpenEmailId(newestId);
    sourcesRef.current?.scrollIntoView({ block: 'start' });
  }, [initialSection, newestId]);

  if (!item) return null;

  const showChange = item.kind === 'change' && item.changes.length > 0;
  // E-12: a new or change event's folded tasks (F-1), else the open tasks
  // a cancellation cancels.
  const subtasks: readonly { id: string; title: string }[] =
    item.tasks.length > 0 ? item.tasks : item.cancelTasks;

  return (
    <FullScreenSheet
      title={
        item.type === 'event'
          ? REVIEW_COPY.detailsEvent
          : REVIEW_COPY.detailsTask
      }
      onClose={onClose}
      leading="close"
      rightAction={
        <button
          type="button"
          aria-label={REVIEW_COPY.detailsEdit}
          onClick={() => {
            if (!editDisabled) onEdit(item);
          }}
          aria-disabled={editDisabled || undefined}
          className="inline-flex cursor-pointer aria-disabled:cursor-not-allowed aria-disabled:opacity-50 items-center justify-center rounded-md p-1 text-muted-foreground transition-colors duration-150 hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          <Pencil size={20} />
        </button>
      }
    >
      <div className="flex flex-col gap-5">
        <h2 className="text-2xl font-bold text-text-heading break-words">
          {item.title}
        </h2>

        {item.whenLabel && (
          <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1 text-sm text-foreground">
            <Calendar
              size={14}
              aria-hidden="true"
            />
            {item.whenLabel}
          </span>
        )}

        {showChange && (
          <div
            data-testid="review-details-change"
            className="rounded-xl bg-surface-muted p-3 flex flex-col gap-2"
          >
            <Badge
              variant="info"
              size="xs"
              className="w-fit"
            >
              {REVIEW_COPY.tagChange}
            </Badge>
            {item.changes.map((c) => (
              <ChangeLine
                key={c.field}
                change={c}
              />
            ))}
          </div>
        )}

        {item.note && (
          <p className="m-0 rounded-md bg-danger-light px-2 py-1 text-sm text-text-error">
            {item.note}
          </p>
        )}

        {item.detailLocation && (
          <DetailRow
            icon={MapPin}
            label={REVIEW_COPY.detailLocation}
          >
            <span className="text-sm text-foreground underline underline-offset-2 break-words">
              {item.detailLocation}
            </span>
          </DetailRow>
        )}

        {item.childNames.length > 0 && (
          <DetailRow
            icon={Users}
            label={REVIEW_COPY.detailChildren}
          >
            <People names={item.childNames} />
          </DetailRow>
        )}

        {memberNames.length > 0 && (
          <DetailRow
            icon={User}
            label={REVIEW_COPY.detailMembers}
          >
            <People names={memberNames} />
          </DetailRow>
        )}

        {subtasks.length > 0 && (
          <DetailRow
            icon={SquareCheckBig}
            label={REVIEW_COPY.detailSubtasks}
          >
            <div className="flex flex-col items-end gap-1">
              {subtasks.map((task) => (
                <span
                  key={task.id}
                  className="text-sm text-foreground break-words"
                >
                  {task.title}
                </span>
              ))}
            </div>
          </DetailRow>
        )}

        <ReviewSources
          ref={sourcesRef}
          emails={sources.emails}
          summary={sources.summary}
          openEmailId={openEmailId}
          onToggleEmail={(id) =>
            setOpenEmailId((current) => (current === id ? null : id))
          }
          loading={sources.loading}
          error={sources.error}
          onRetry={sources.reload}
          expectedCount={item.sourceEmailIds.length}
        />
      </div>
    </FullScreenSheet>
  );
}
